import type { FC, ReactNode } from 'react'
import type { DiagramKey } from '@/types/content'

import { cn } from '@/lib/utils'

const FRAME = '#0b2b4a'
const GLASS = '#dceefc'
const GLASS_EDGE = '#9cc9ef'
const ACCENT = '#005ca6'
const WALL = '#efe6d6'

const LABELS: Record<DiagramKey, string> = {
    'double-hung': 'Drawing of a double-hung window: two sashes that both slide up and down',
    casement: 'Drawing of a casement window: one sash hinged on the side that cranks outward',
    sliding: 'Drawing of a sliding window: one sash slides sideways past the other',
    picture: 'Drawing of a picture window: one large fixed pane that does not open',
    awning: 'Drawing of an awning window: a sash hinged at the top that opens outward from the bottom',
    'bay-bow': 'Drawing of a bay window: a large center window with angled side windows projecting from the wall',
    garden: 'Drawing of a garden window: a small glass box that projects out from the wall',
    shutters: 'Drawing of a window with exterior louvered shutters on each side',
    'storm-window': 'Drawing of an exterior storm window: a second glass panel fitted outside the existing window',
    'entry-door': 'Drawing of an exterior entry door with a glass sidelight',
    'patio-door': 'Drawing of a sliding patio door: one large glass panel slides past the other',
    'french-door': 'Drawing of exterior French doors: a hinged pair of glass doors meeting in the middle',
    'storm-door': 'Drawing of a storm door: a glass and screen door fitted outside the front door'
}

const Arrow: FC<{ x1: number, y1: number, x2: number, y2: number }> = ({ x1, y1, x2, y2 }): ReactNode => (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={ACCENT} strokeWidth={3} strokeLinecap='round' markerEnd='url(#acm-arrow)' markerStart='url(#acm-arrow)' />
)

const Pane: FC<{ x: number, y: number, w: number, h: number }> = ({ x, y, w, h }): ReactNode => (
    <rect x={x} y={y} width={w} height={h} fill={GLASS} stroke={GLASS_EDGE} strokeWidth={1.5} />
)

const Swing: FC<{ points: string }> = ({ points }): ReactNode => (
    <polyline points={points} fill='none' stroke={ACCENT} strokeWidth={2} strokeDasharray='6 5' />
)

const shapes: Record<DiagramKey, ReactNode> = {
    'double-hung': (
        <>
            <rect x={55} y={14} width={110} height={142} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={63} y={22} width={94} height={62} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={69} y={28} w={82} h={50} />
            <rect x={63} y={84} width={94} height={64} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={69} y={90} w={82} h={52} />
            <Arrow x1={182} y1={36} x2={182} y2={134} />
        </>
    ),
    casement: (
        <>
            <rect x={55} y={14} width={110} height={142} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={63} y={22} width={94} height={126} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={69} y={28} w={82} h={114} />
            <Swing points='151,28 69,85 151,142' />
            <rect x={60} y={40} width={4} height={14} fill={FRAME} />
            <rect x={60} y={116} width={4} height={14} fill={FRAME} />
        </>
    ),
    sliding: (
        <>
            <rect x={20} y={36} width={180} height={98} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={28} y={44} width={84} height={82} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={34} y={50} w={72} h={70} />
            <rect x={108} y={44} width={84} height={82} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={114} y={50} w={72} h={70} />
            <Arrow x1={44} y1={150} x2={176} y2={150} />
        </>
    ),
    picture: (
        <>
            <rect x={20} y={28} width={180} height={114} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <Pane x={30} y={38} w={160} h={94} />
            <line x1={60} y1={52} x2={88} y2={80} stroke='#fff' strokeWidth={4} strokeLinecap='round' />
            <line x1={74} y1={50} x2={110} y2={86} stroke='#fff' strokeWidth={3} strokeLinecap='round' />
        </>
    ),
    awning: (
        <>
            <rect x={35} y={40} width={150} height={92} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={43} y={48} width={134} height={76} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={49} y={54} w={122} h={64} />
            <Swing points='49,118 110,54 171,118' />
            <rect x={70} y={44} width={14} height={4} fill={FRAME} />
            <rect x={136} y={44} width={14} height={4} fill={FRAME} />
            <Arrow x1={110} y1={150} x2={110} y2={162} />
        </>
    ),
    'bay-bow': (
        <>
            <polygon points='10,40 62,30 62,140 10,130' fill='#fff' stroke={FRAME} strokeWidth={3.5} />
            <polygon points='18,47 55,40 55,131 18,124' fill={GLASS} stroke={GLASS_EDGE} strokeWidth={1.5} />
            <rect x={62} y={30} width={96} height={110} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <Pane x={70} y={38} w={80} h={94} />
            <polygon points='158,30 210,40 210,130 158,140' fill='#fff' stroke={FRAME} strokeWidth={3.5} />
            <polygon points='165,40 202,47 202,124 165,131' fill={GLASS} stroke={GLASS_EDGE} strokeWidth={1.5} />
            <polygon points='4,40 62,22 158,22 216,40' fill={WALL} stroke={FRAME} strokeWidth={3} />
        </>
    ),
    garden: (
        <>
            <rect x={20} y={20} width={180} height={130} fill={WALL} />
            <polygon points='60,58 90,40 150,40 180,58' fill={GLASS} stroke={FRAME} strokeWidth={3} />
            <rect x={60} y={58} width={120} height={82} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <Pane x={68} y={66} w={104} h={66} />
            <polygon points='40,52 60,58 60,140 40,130' fill={GLASS} stroke={FRAME} strokeWidth={2.5} />
            <line x1={68} y1={118} x2={172} y2={118} stroke={FRAME} strokeWidth={3} />
        </>
    ),
    shutters: (
        <>
            <rect x={75} y={18} width={70} height={134} rx={2} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <Pane x={82} y={25} w={56} h={56} />
            <Pane x={82} y={89} w={56} h={56} />
            {[0, 1].map(side => (
                <g key={side}>
                    <rect x={side === 0 ? 28 : 154} y={18} width={38} height={134} rx={2} fill={FRAME} />
                    {Array.from({ length: 12 }).map((_, index) => (
                        <line
                            key={index}
                            x1={side === 0 ? 33 : 159}
                            y1={28 + index * 10}
                            x2={side === 0 ? 61 : 187}
                            y2={28 + index * 10}
                            stroke='#3d6a94'
                            strokeWidth={3}
                        />
                    ))}
                </g>
            ))}
        </>
    ),
    'storm-window': (
        <>
            <rect x={48} y={10} width={124} height={150} rx={3} fill='none' stroke={ACCENT} strokeWidth={4} />
            <rect x={58} y={20} width={104} height={130} rx={3} fill='#fff' stroke={FRAME} strokeWidth={3.5} />
            <Pane x={66} y={28} w={88} h={52} />
            <Pane x={66} y={90} w={88} h={52} />
            <line x1={58} y1={85} x2={162} y2={85} stroke={FRAME} strokeWidth={3} />
            <rect x={44} y={6} width={132} height={158} rx={5} fill='none' stroke={ACCENT} strokeWidth={1.5} strokeDasharray='5 4' />
        </>
    ),
    'entry-door': (
        <>
            <rect x={50} y={10} width={130} height={150} fill={WALL} stroke={FRAME} strokeWidth={3} />
            <rect x={58} y={18} width={22} height={142} fill={GLASS} stroke={FRAME} strokeWidth={3} />
            <rect x={86} y={18} width={86} height={142} fill='#b9834a' stroke={FRAME} strokeWidth={4} />
            <Pane x={96} y={28} w={66} h={38} />
            <rect x={96} y={76} width={28} height={70} fill='none' stroke='#8a5c2c' strokeWidth={2.5} />
            <rect x={134} y={76} width={28} height={70} fill='none' stroke='#8a5c2c' strokeWidth={2.5} />
            <rect x={160} y={84} width={5} height={24} rx={2} fill={FRAME} />
            <rect x={44} y={158} width={142} height={6} fill={FRAME} />
        </>
    ),
    'patio-door': (
        <>
            <rect x={16} y={14} width={188} height={146} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={24} y={22} width={88} height={130} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={30} y={28} w={76} h={118} />
            <rect x={108} y={22} width={88} height={130} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={114} y={28} w={76} h={118} />
            <rect x={116} y={80} width={4} height={22} rx={2} fill={FRAME} />
            <Arrow x1={40} y1={172} x2={180} y2={172} />
        </>
    ),
    'french-door': (
        <>
            <rect x={36} y={12} width={148} height={148} rx={3} fill='#fff' stroke={FRAME} strokeWidth={4} />
            <rect x={44} y={20} width={64} height={136} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={50} y={26} w={52} h={124} />
            <rect x={112} y={20} width={64} height={136} fill='#fff' stroke={FRAME} strokeWidth={3} />
            <Pane x={118} y={26} w={52} h={124} />
            <rect x={102} y={82} width={4} height={20} rx={2} fill={FRAME} />
            <rect x={114} y={82} width={4} height={20} rx={2} fill={FRAME} />
            <Swing points='50,26 102,88 50,150' />
            <Swing points='170,26 118,88 170,150' />
        </>
    ),
    'storm-door': (
        <>
            <rect x={56} y={10} width={108} height={150} fill='#7a4f2a' stroke={FRAME} strokeWidth={3} />
            <rect x={48} y={6} width={124} height={156} rx={4} fill='none' stroke={FRAME} strokeWidth={5} />
            <rect x={60} y={16} width={100} height={92} fill={GLASS} fillOpacity={0.75} stroke={ACCENT} strokeWidth={2.5} />
            <rect x={60} y={112} width={100} height={42} fill='#ffffff' fillOpacity={0.6} stroke={ACCENT} strokeWidth={2.5} />
            <rect x={150} y={96} width={5} height={22} rx={2} fill={FRAME} />
            <rect x={42} y={160} width={136} height={5} fill={FRAME} />
        </>
    )
}

interface WindowDiagramProps {
    diagram: DiagramKey
    className?: string
}

const WindowDiagram: FC<WindowDiagramProps> = ({ diagram, className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 220 180'
            role='img'
            aria-label={LABELS[diagram]}
            className={cn('h-auto w-full', className)}
        >
            <defs>
                <marker id='acm-arrow' viewBox='0 0 10 10' refX='5' refY='5' markerWidth='5' markerHeight='5' orient='auto-start-reverse'>
                    <path d='M0,0 L10,5 L0,10 z' fill={ACCENT} />
                </marker>
            </defs>

            {shapes[diagram]}
        </svg>
    )
}

export default WindowDiagram
